import type { ComponentProps, PropsWithChildren } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type {
  BorderRadiusProps,
  BottomProps,
  OpacityProps,
  OverflowProps,
  PositionProps,
  RightProps,
  SizeProps,
} from 'styled-system';
import {
  backgroundColor as bgColor,
  borderRadius,
  bottom,
  opacity,
  overflow,
  position,
  right,
  size,
  space,
} from 'styled-system';

import { useLocalization } from 'common/providers/LocaleProvider';
import { getEnterHandler } from 'common/utils/keyboard';
import { isTouchDevice } from '../DeviceDetector';
import { useLoadingImageBackground } from '../hooks/image';
import { useTrackMousePosition } from '../hooks/mouse';
import { useDoubleTap } from '../hooks/tap';
import type { BackgroundColorProps, SpaceProps } from '../theme/system-types';
import type { ObjectFitProps, TransitionProps } from '../theme/system-utilities';
import { objectFit, transition } from '../theme/system-utilities';
import type { DispatchState } from '../types/React';
import { getSafeEventHandlerOpts } from '../utils/touchEvent';
import { defaultPreloadImage as preloadImage } from './LazyImage/utils';

const TOUCH_EVENT_OPTIONS = getSafeEventHandlerOpts({
  capture: true,
  passive: false,
});

interface ImageZoomProps extends PropsWithChildren {
  onZoomToggle?: (enabled: boolean) => void;
  isActive: boolean;
  src: string;
  zoom: number;
  withKeyboardZoom?: boolean;
}

interface UseTouchZoomArgs {
  ref: Element;
  isActive: boolean;
  minZoom?: number;
  maxZoom?: number;
  maxDistance?: number;
}

const translatePoint = (point: number, zoom: number) => {
  const centerInPercentage = 50;
  const multiplicityMagnify = zoom - 1;
  const translatedPoint = (point - centerInPercentage) * multiplicityMagnify;

  return translatedPoint > 0 ? translatedPoint : -Math.abs(translatedPoint);
};

const calcDistance = touches => {
  const [{ clientX: x1, clientY: y1 }, { clientX: x2, clientY: y2 }] = touches;

  const x = Math.pow(x1 - x2, 2);
  const y = Math.pow(y1 - y2, 2);

  return Math.sqrt(x + y);
};

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const UnstyledTouchArea = styled.div<SizeProps>`
  ${size};
`;

const TouchArea = (props: ComponentProps<typeof UnstyledTouchArea>) => (
  <UnstyledTouchArea size="100%" {...props} />
);

const UnstyledZoomArea = styled.div<
  BackgroundColorProps &
    OpacityProps &
    OverflowProps &
    PositionProps &
    SpaceProps &
    SizeProps &
    BorderRadiusProps &
    Partial<Pick<ImageZoomProps, 'isActive'>> & {
      ref?: DispatchState<React.SetStateAction<HTMLElement>>;
      'data-testid'?: string;
    }
>`
  ${bgColor};
  ${opacity};
  ${overflow};
  ${position};
  ${space};
  ${size};
  ${borderRadius};
  -webkit-overflow-scrolling: touch;
  ${({ isActive }) =>
    isActive
      ? css`
          cursor: zoom-out;
        `
      : css`
          cursor: zoom-in;
        `}
`;

const ZoomArea = (props: ComponentProps<typeof UnstyledZoomArea>) => (
  <UnstyledZoomArea
    data-testid="zoom-area"
    overflow="visible"
    position="relative"
    opacity={1}
    m="sp_0"
    size="inherit"
    borderRadius="medium"
    {...props}
  />
);

const UnstyledMagnifier = styled.img<
  PositionProps &
    BackgroundColorProps &
    BottomProps &
    RightProps &
    OpacityProps &
    ObjectFitProps &
    SizeProps &
    TransitionProps & { center?: boolean }
>`
  ${position};
  ${bottom};
  ${right};
  ${opacity};
  ${objectFit};
  ${size};
  ${transition};
`;

const Magnifier = ({
  zoom,
  center,
  style,
  ...rest
}: ComponentProps<typeof UnstyledMagnifier> & { zoom: number }) => (
  <UnstyledMagnifier
    position="absolute"
    objectFit="contain"
    size="100%"
    opacity={1}
    bottom={0}
    right={0}
    transition="transform 0.2s ease-in-out"
    style={{ transform: `scale(${zoom})`, transformOrigin: center ? '50%' : 'top left', ...style }}
    {...rest}
  />
);

/* eslint-disable max-lines, max-lines-per-function */
const usePinchZoom = ({
  isActive,
  ref,
  minZoom = 1,
  maxZoom = 2,
  maxDistance = 100,
}: UseTouchZoomArgs) => {
  const [currentZoom, setCurrentZoom] = useState(minZoom);
  const [initDistance, setInitDistance] = useState<number>();
  const [distance, setDistance] = useState<number>();
  const [scaling, setScaling] = useState(false);
  const [doubleTap, setDoubleTap] = useState(false);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const panStartRef = useRef({ x: 0, y: 0 });
  const lastPanOffsetRef = useRef({ x: 0, y: 0 });
  const currentZoomRef = useRef(minZoom);

  // Use refs to store the latest ref value to avoid stale closures
  const elementRef = useRef(ref);
  useEffect(() => {
    elementRef.current = ref;
  }, [ref]);

  const resetPanOffset = useCallback(() => {
    setPanOffset({ x: 0, y: 0 });
    lastPanOffsetRef.current = { x: 0, y: 0 };
  }, []);

  const onTap = useDoubleTap(
    useCallback(() => {
      setDoubleTap(true);
    }, [])
  );

  useEffect(() => {
    if (doubleTap) {
      const isDefaultSize = currentZoom === minZoom;
      setDoubleTap(false);
      setCurrentZoom(isDefaultSize ? maxZoom : minZoom);
      setScaling(isDefaultSize);
      if (!isDefaultSize) {
        resetPanOffset();
      }
    }
  }, [doubleTap, currentZoom, maxZoom, minZoom, resetPanOffset]);

  useEffect(() => {
    if (currentZoom === minZoom) {
      setScaling(false);
      resetPanOffset();
    }
  }, [currentZoom, minZoom, resetPanOffset]);

  // Reset zoom state when slide becomes inactive (user navigated away)
  useEffect(() => {
    if (!isActive) {
      setCurrentZoom(minZoom);
      setScaling(false);
      resetPanOffset();
    }
  }, [isActive, minZoom, resetPanOffset]);

  // Keep currentZoomRef in sync with currentZoom for use in event handlers
  useEffect(() => {
    currentZoomRef.current = currentZoom;
  }, [currentZoom]);

  useEffect(() => {
    if (scaling && distance && initDistance) {
      const absoluteDistance = Math.abs(distance - initDistance);
      const fixedDistance = absoluteDistance > maxDistance ? maxDistance : absoluteDistance;
      const availableZoom = maxZoom - minZoom;
      const zoomRatio = availableZoom * (fixedDistance / maxDistance);

      if (distance - initDistance > 0) {
        setCurrentZoom(minZoom + zoomRatio);
      } else {
        setCurrentZoom(maxZoom - zoomRatio);
      }
    }
  }, [scaling, initDistance, distance]);

  const onPinchMove = useCallback(event => {
    if (elementRef.current && event.touches.length === 2) {
      setDistance(calcDistance(event.touches));
    }

    event.preventDefault();
  }, []);

  const onPinchEnd = useCallback(
    event => {
      if (elementRef.current) {
        elementRef.current.removeEventListener('touchmove', onPinchMove, TOUCH_EVENT_OPTIONS);
        elementRef.current.removeEventListener('touchend', onPinchEnd, TOUCH_EVENT_OPTIONS);
      }

      event.preventDefault();

      setInitDistance(null);
      setDistance(null);
    },
    [onPinchMove]
  );

  const onPinchStart = useCallback(
    event => {
      if (elementRef.current && event.touches.length === 2) {
        elementRef.current.addEventListener('touchmove', onPinchMove, TOUCH_EVENT_OPTIONS);
        elementRef.current.addEventListener('touchend', onPinchEnd, TOUCH_EVENT_OPTIONS);

        setScaling(true);
        setInitDistance(calcDistance(event.touches));
        event.preventDefault();
        event.stopPropagation(); // Prevent swipe handler from triggering navigation
      } else {
        onTap(event);
      }
    },
    [onPinchMove, onPinchEnd, onTap]
  );

  const onPanMove = useCallback(event => {
    if (event.touches.length === 1 && elementRef.current) {
      const touch = event.touches[0];
      const deltaX = touch.clientX - panStartRef.current.x;
      const deltaY = touch.clientY - panStartRef.current.y;

      // Calculate pan boundaries based on zoom level and container size
      // With transformOrigin: 50%, the extra zoomed area extends equally in all directions
      const containerRect = elementRef.current.getBoundingClientRect();
      const maxPanX = (containerRect.width * (currentZoomRef.current - 1)) / 2;
      const maxPanY = (containerRect.height * (currentZoomRef.current - 1)) / 2;

      // Calculate new offset and clamp to boundaries
      const newX = lastPanOffsetRef.current.x + deltaX;
      const newY = lastPanOffsetRef.current.y + deltaY;

      setPanOffset({
        x: clamp(newX, -maxPanX, maxPanX),
        y: clamp(newY, -maxPanY, maxPanY),
      });
    }

    event.preventDefault();
    event.stopPropagation();
  }, []);

  const onPanEnd = useCallback(
    event => {
      if (elementRef.current) {
        elementRef.current.removeEventListener('touchmove', onPanMove, TOUCH_EVENT_OPTIONS);
        elementRef.current.removeEventListener('touchend', onPanEnd, TOUCH_EVENT_OPTIONS);
      }

      // Update lastPanOffsetRef to current pan position for next pan gesture
      setPanOffset(current => {
        lastPanOffsetRef.current = { ...current };

        return current;
      });

      event.preventDefault();
      event.stopPropagation();
    },
    [onPanMove]
  );

  const onPanStart = useCallback(
    event => {
      if (elementRef.current && event.touches.length === 1) {
        elementRef.current.addEventListener('touchmove', onPanMove, TOUCH_EVENT_OPTIONS);
        elementRef.current.addEventListener('touchend', onPanEnd, TOUCH_EVENT_OPTIONS);

        const touch = event.touches[0];
        panStartRef.current = { x: touch.clientX, y: touch.clientY };

        event.preventDefault();
        event.stopPropagation();
      }
    },
    [onPanMove, onPanEnd]
  );

  useEffect(() => {
    // Enable panning when zoomed in
    const isZoomedIn = currentZoom > minZoom;
    if (ref) {
      if (isZoomedIn) {
        ref.addEventListener('touchstart', onPanStart, TOUCH_EVENT_OPTIONS);
      } else {
        ref.removeEventListener('touchstart', onPanStart, TOUCH_EVENT_OPTIONS);
      }
    }

    return () => {
      if (ref) {
        ref.removeEventListener('touchstart', onPanStart, TOUCH_EVENT_OPTIONS);
      }
    };
  }, [currentZoom, minZoom, ref, onPanStart]);

  useEffect(() => {
    if (isActive && ref) {
      ref.addEventListener('touchstart', onPinchStart, TOUCH_EVENT_OPTIONS);
    }

    return () => {
      if (ref) {
        ref.removeEventListener('touchstart', onPinchStart, TOUCH_EVENT_OPTIONS);
      }
    };
  }, [isActive, ref, onPinchStart]);

  return { currentZoom, isScaling: scaling, panOffset };
};

const PinchZoom = ({ children, onZoomToggle, isActive, zoom, src }: ImageZoomProps) => {
  const [touchAreaRef, setTouchAreaRef] = useState<HTMLDivElement>();
  const { currentZoom, isScaling, panOffset } = usePinchZoom({
    maxZoom: zoom,
    isActive,
    ref: touchAreaRef,
  });

  useEffect(() => {
    if (onZoomToggle) {
      onZoomToggle(isScaling);
    }
  }, [isScaling]);

  return (
    <TouchArea ref={setTouchAreaRef} data-testid="touch-area">
      <ZoomArea isActive={isScaling} data-testid="pinch-zoom-area">
        <Magnifier
          data-testid="zoom-magnifier"
          src={src}
          alt=""
          zoom={currentZoom}
          center
          style={{ translate: `${panOffset.x}px ${panOffset.y}px` }}
        />
        {children}
      </ZoomArea>
    </TouchArea>
  );
};

const useHoverZoom = (resizedSrc: string, isScaling: boolean) => {
  const [src, setSrc] = useState(resizedSrc);

  useEffect(() => {
    if (!isScaling) {
      return;
    }

    let isAlive = true;
    const preloadOriginalImage = async () => {
      const originalSrc = resizedSrc.replace(/\/\d+x\d+\//, '/original/');

      try {
        await preloadImage(originalSrc);

        if (isAlive) {
          setSrc(originalSrc);
        }
      } catch {
        // TODO: error handler.
      }
    };

    setSrc(resizedSrc);
    preloadOriginalImage();

    return () => {
      isAlive = false;
    };
  }, [resizedSrc, isScaling]);

  return [src];
};

interface GetStyleProps extends Pick<ImageZoomProps, 'zoom'> {
  cursorPosition: ReturnType<typeof useTrackMousePosition>['cursorPosition'];
}

const getMagnifierStyle = ({ zoom, cursorPosition }: GetStyleProps) => ({
  bottom: `${translatePoint(cursorPosition.y, zoom)}%`,
  right: `${translatePoint(cursorPosition.x, zoom)}%`,
});

const useAreaClick = ({ onZoomToggle }: Pick<ImageZoomProps, 'onZoomToggle'>) => {
  const [isScaling, setScaling] = useState(false);

  const onAreaClick = useCallback(() => {
    setScaling(!isScaling);
    if (onZoomToggle) {
      onZoomToggle(!isScaling);
    }
  }, [isScaling]);

  return { isScaling, onAreaClick };
};

const HoverZoom = ({
  onZoomToggle,
  children,
  isActive,
  zoom,
  src: defaultSrc,
  withKeyboardZoom,
}: ImageZoomProps) => {
  const { t } = useLocalization();
  const { onAreaClick, isScaling } = useAreaClick({
    onZoomToggle,
  });
  const [src] = useHoverZoom(defaultSrc, isScaling);
  const { cursorPosition, ...events } = useTrackMousePosition();
  const { backgroundColor, onImageLoad } = useLoadingImageBackground(!isActive);
  const handleKeyDown = getEnterHandler(e => {
    e.preventDefault();
    onAreaClick();
  });

  useEffect(() => {
    if (!withKeyboardZoom) {
      return;
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [withKeyboardZoom, onAreaClick]);

  return (
    <ZoomArea
      onClick={isActive ? onAreaClick : null}
      isActive={isScaling}
      data-testid="hover-zoom-area"
      backgroundColor={backgroundColor}
      {...(isScaling ? events : {})}
    >
      {isScaling ? (
        <Magnifier
          data-testid="zoom-magnifier"
          style={getMagnifierStyle({ zoom, cursorPosition })}
          src={src}
          zoom={zoom}
          center={true}
          onLoad={onImageLoad}
          alt={t('h24_zoomed_in_picture')}
        />
      ) : (
        children
      )}
    </ZoomArea>
  );
};

export const ImageZoom = (props: ImageZoomProps) =>
  isTouchDevice() ? <PinchZoom {...props} /> : <HoverZoom {...props} />;
